import { useDeck } from '@/app/context/DeckContext/useDeck';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import MenuNavigation from './menu-navigation/MenuNavigation';
import { DeckPages } from './models';
import UserShareIcon from '@/app/icons/UserShareIcon';
import { DeckStatus } from '@/app/constants/DeckStatus';
import { useAnalytics } from '@/app/context/AnalyticsContext/useAnalytics';

interface IProps {
  onBack: () => void;
  currentPage: DeckPages;
  enableSharing?: boolean;
}

export default function SelectedDeckHeader({
  onBack,
  currentPage,
  enableSharing
}: IProps) {
  const [isEditingName, setIsEditingName] = useState(false);
  const [editName, setEditName] = useState('');
  const inputElement = useRef(null);

  const { trackEvent, analyticsInstance } = useAnalytics();

  const router = useRouter();

  useEffect(() => {
    const queryParams = new URLSearchParams(router.query as any);

    if (queryParams.has('rename')) {
      queryParams.delete('rename');

      setIsEditingName(true);

      router.replace({
        search: queryParams.toString()
      });
    }
  }, []);

  useEffect(() => {
    if (inputElement.current && isEditingName) {
      inputElement.current.focus();
    }
  }, [isEditingName]);

  const { isSavingChange, selectedDeck, deleteDeck, editDeckName } = useDeck();

  useEffect(() => {
    setEditName(selectedDeck?.name || '');
  }, [selectedDeck.name]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      onSaveName();
    }
  };

  const onSaveName = () => {
    trackEvent(analyticsInstance, 'deck_header_edit_name', {
      deck_id: selectedDeck.id
    });

    if (editName !== selectedDeck.name && editName.length < 500) {
      editDeckName(selectedDeck.id, editName);
    }
    setIsEditingName(false);
  };

  const onDelete = () => {
    trackEvent(analyticsInstance, 'deck_header_click_delete', {
      deck_id: selectedDeck.id
    });

    deleteDeck(selectedDeck.id);
  };

  return (
    <div
      id="selected-header"
      className="navbar fixed top-16 z-40 w-full bg-base-300 pt-2 text-base-content shadow-lg lg:pt-3"
    >
      <div style={{ height: 48, width: 48 }} className="flex-none">
        <button
          onClick={() => {
            trackEvent(analyticsInstance, 'deck_header_click_back', {
              deck_id: selectedDeck.id
            });
            onBack();
          }}
          className="btn btn-square btn-ghost"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            className="inline-block h-6 w-6 stroke-current"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            ></path>
          </svg>
        </button>
      </div>
      <div className="align-center mx-2 flex w-0 flex-1 px-2 md:pr-12">
        <div
          onClick={() => setIsEditingName(true)}
          className="overflow-hidden text-ellipsis whitespace-nowrap font-bold"
        >
          {!isEditingName && (selectedDeck ? selectedDeck.name : '...')}
        </div>
        <input
          type="text"
          autoFocus
          style={{ display: selectedDeck && isEditingName ? 'unset' : 'none' }}
          ref={inputElement}
          onChange={(ev) => setEditName(ev.target.value || '')}
          onBlur={onSaveName}
          onKeyDown={handleKeyDown}
          placeholder={selectedDeck?.name}
          value={editName}
          className="input input-bordered input-xs w-full max-w-xs bg-white text-black md:w-1/2"
        />
        {selectedDeck?.language && selectedDeck?.language.name && (
          <div
            style={{ marginTop: 2 }}
            className="ml-2 mt-1 hidden break-normal rounded text-sm font-bold text-secondary-content lg:block"
          >
            ({selectedDeck?.language.name})
          </div>
        )}
      </div>
      <div
        className={`hidden md:block ${
          isSavingChange ? 'visible' : 'invisible'
        }`}
      >
        Saving...
      </div>
      {currentPage !== DeckPages.Home && currentPage !== DeckPages.Share && (
        <div className="hidden lg:block">
          <MenuNavigation currentPage={currentPage} />
        </div>
      )}

      {currentPage === DeckPages.Home &&
        selectedDeck.status === DeckStatus.Complete &&
        !selectedDeck.isImageOcclusion && (
          <div
            data-tip={`${
              !selectedDeck.sharedById
                ? 'Share Deck'
                : 'Shared decks cannot be reshared'
            }`}
            className="tooltip tooltip-left"
          >
            <div
              onClick={() => {
                if (!selectedDeck.sharedById) {
                  trackEvent(analyticsInstance, 'deck_header_click_share', {
                    deck_id: selectedDeck.id
                  });
                  router.push(`/app/${selectedDeck.id}/share`);
                }
              }}
              className={`btn ${
                selectedDeck.sharedById ? 'btn-disable opacity-50' : ''
              } cursor-pointer border-transparent bg-transparent`}
            >
              <UserShareIcon />
            </div>
          </div>
        )}
      {/* {enableSharing &&
        selectedDeck.sharedById &&
        currentPage === DeckPages.Home && (
          <div className="mr-2 mb-1 hidden text-sm text-secondary-content lg:block">
            Shared by {selectedDeck.sharedBy}
          </div>
        )} */}
      <div style={{ height: 48, width: 48 }} className="ml-2 flex-none">
        <div className="dropdown dropdown-end">
          <button className="btn btn-square btn-ghost">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              className="inline-block h-6 w-6 fill-base-content text-base-content"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M5 12h.01M12 12h.01M19 12h.01M6 12a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0zm7 0a1 1 0 11-2 0 1 1 0 012 0z"
              ></path>
            </svg>
          </button>
          <ul
            tabIndex={0}
            className="dropdown-content menu rounded-box z-50 w-52 bg-neutral p-2 text-neutral-content shadow md:hidden"
          >
            {currentPage !== DeckPages.Home && currentPage !== DeckPages.Share && (
              <>
                <li>
                  <a
                    onClick={() => {
                      trackEvent(analyticsInstance, 'deck_header_click_quiz', {
                        deck_id: selectedDeck.id
                      });
                      router.replace(`/app/${selectedDeck.id}/quiz`);
                    }}
                  >
                    Multiple Choice
                  </a>
                </li>
                <li>
                  <a
                    onClick={() => {
                      trackEvent(
                        analyticsInstance,
                        'deck_header_click_flashcards',
                        {
                          deck_id: selectedDeck.id
                        }
                      );

                      router.replace(`/app/${selectedDeck.id}/flashcards`);
                    }}
                  >
                    Flashcards
                  </a>
                </li>
                <li>
                  <a
                    onClick={() => {
                      trackEvent(
                        analyticsInstance,
                        'deck_header_click_study_guide',
                        {
                          deck_id: selectedDeck.id
                        }
                      );
                      router.replace(`/app/${selectedDeck.id}/studyGuide`);
                    }}
                  >
                    Study Guide
                  </a>
                </li>
                <li>
                  <a
                    onClick={() => {
                      trackEvent(analyticsInstance, 'deck_header_click_edit', {
                        deck_id: selectedDeck.id
                      });
                      router.replace(`/app/${selectedDeck.id}/edit`);
                    }}
                  >
                    View All / Edit
                  </a>
                </li>
              </>
            )}
            <li>
              <a onClick={onDelete}>Delete Deck</a>
            </li>
          </ul>
          <ul
            tabIndex={0}
            className="dropdown-content menu rounded-box z-50 hidden w-52 bg-neutral p-2 text-neutral-content shadow md:flex lg:hidden"
          >
            {currentPage !== DeckPages.Home && currentPage !== DeckPages.Share && (
              <>
                <li>
                  <a
                    onClick={() => {
                      trackEvent(analyticsInstance, 'deck_header_click_quiz', {
                        deck_id: selectedDeck.id
                      });
                      router.replace(`/app/${selectedDeck.id}/quiz`);
                    }}
                  >
                    Multiple Choice
                  </a>
                </li>
                <li>
                  <a
                    onClick={() => {
                      trackEvent(
                        analyticsInstance,
                        'deck_header_click_flashcards',
                        {
                          deck_id: selectedDeck.id
                        }
                      );
                      router.replace(`/app/${selectedDeck.id}/flashcards`);
                    }}
                  >
                    Flashcards
                  </a>
                </li>
                <li>
                  <a
                    onClick={() => {
                      trackEvent(
                        analyticsInstance,
                        'deck_header_click_study_guide',
                        {
                          deck_id: selectedDeck.id
                        }
                      );
                      router.replace(`/app/${selectedDeck.id}/studyGuide`);
                    }}
                  >
                    Study Guide
                  </a>
                </li>
                <li>
                  <a
                    onClick={() => {
                      trackEvent(analyticsInstance, 'deck_header_click_edit', {
                        deck_id: selectedDeck.id
                      });
                      router.replace(`/app/${selectedDeck.id}/edit`);
                    }}
                  >
                    View All / Edit
                  </a>
                </li>
              </>
            )}

            <li>
              <a onClick={onDelete}>Delete Deck</a>
            </li>
          </ul>
          <ul
            tabIndex={0}
            className="dropdown-content menu rounded-box z-50 hidden w-52 bg-neutral p-2 text-neutral-content shadow lg:flex"
          >
            <li>
              <a onClick={onDelete}>Delete Deck</a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
